import { findCurrencyMinorUnits } from '../currencies.ts';
import type { ListColumnType } from './contract.ts';

/**
 * Formeln der Massenbearbeitung (Story 1.30, AD-32 Ergänzung 04.10.2026, EXPERIENCE §4.12) als reine Funktionen:
 * eigener kleiner Parser mit Allowlist (Muster AD-34), nie `eval`, `Function` oder Objekt-Zugriff über Namen. Ohne
 * Datenbank, Uhr und Node-Abhängigkeiten (auch die Web-App prüft damit beim Tippen).
 *
 * Erlaubt: Zahlen mit Komma oder Punkt (`1,8`), `+ − × ÷` (auch `- * /`), Klammern, Spaltenbezüge `{Beschriftung}`
 * (nur Spalten der Liste mit Zahlentyp), `runden(x; n)`, `min(a; b; …)`, `max(a; b; …)` und `wenn(bedingung; a; b)`
 * mit `< <= > >= = <>` (auch `≤ ≥ ≠`). Argumente trennt `;` (wie in Excel auf Deutsch).
 *
 * Rechnen exakt über BigInt (Dezimalzahl = ganze Zahl × 10^-Stellen): Teilen intern mit 10 Nachkommastellen,
 * kaufmännisch gerundet (half-up, weg von null); das Ergebnis wird half-up auf die Zielspalte gebracht (Betrag: kleinste
 * Einheit der Währung, sonst 4 Nachkommastellen). Beträge stehen in der Formel in Währungseinheiten (`12,00`).
 *
 * `runden(x; n)`: ganze Zahl n von 0 bis 4 = so viele Nachkommastellen; 0 < n < 1 = Endung (`0,90` → nächstgelegener
 * Wert mit ,90; bei Gleichstand aufwärts, wie „runden auf ,90“ der Feld-Leiste); ohne n = ganze Zahl.
 */

export const FORMULA_TEXTS = {
  empty: 'Bitte eine Formel eingeben, z. B. runden({EK} × 1,8; 0,90).',
  tooLong: (max: number) => `Die Formel ist zu lang (höchstens ${max} Zeichen).`,
  tooDeep: 'Die Formel ist zu tief verschachtelt.',
  unknownColumn: (name: string) => `Unbekannte Spalte ‚${name}‘`,
  ambiguousColumn: (name: string) => `Die Spalte ‚${name}‘ gibt es mehrfach – bitte die Spaltenwahl („{“) nutzen.`,
  columnNotNumber: (name: string) => `Die Spalte ‚${name}‘ enthält keine Zahl; in Formeln gehen nur Zahlen, Beträge, Mengen und Prozent.`,
  unclosedColumn: 'Nach „{“ fehlt die schließende „}“.',
  emptyColumn: 'Zwischen „{“ und „}“ fehlt der Spaltenname.',
  unknownFunction: (name: string) => `Unbekannte Funktion ‚${name}‘ – erlaubt sind runden, min, max und wenn.`,
  badChar: (char: string) => `Das Zeichen ‚${char}‘ ist in Formeln nicht erlaubt.`,
  badNumber: (text: string) => `‚${text}‘ ist keine gültige Zahl.`,
  ambiguousNumber: (text: string) => `‚${text}‘ ist mehrdeutig: bitte ohne Tausenderpunkt (${text.replace('.', '')}) oder mit Komma (${text.replace('.', ',')}) schreiben.`,
  expected: (what: string) => `Hier fehlt ${what}.`,
  unexpectedEnd: 'Die Formel endet zu früh.',
  trailing: (token: string) => `Unerwartet: ‚${token}‘.`,
  missingParen: 'Eine Klammer „)“ fehlt.',
  rundenArgs: 'runden braucht einen Wert und optional die Stellen: runden(x; 2) oder runden(x; 0,90).',
  rundenDigits: 'Bei runden bitte 0 bis 4 Stellen oder eine Endung wie 0,90 bzw. 0,99 angeben.',
  minMaxArgs: (fn: string) => `${fn} braucht mindestens zwei Werte, z. B. ${fn}({EK} × 1,3; 5).`,
  wennArgs: 'wenn braucht drei Teile: wenn(bedingung; dann; sonst).',
  conditionExpected: 'Der erste Teil von wenn muss ein Vergleich sein, z. B. {EK} > 10.',
  comparisonOutside: 'Vergleiche (<, >, = …) gehen nur im ersten Teil von wenn.',
  valueEmpty: (name: string) => `‚${name}‘ ist leer – die Formel braucht hier einen Wert.`,
  valueInvalid: (name: string) => `‚${name}‘ hat keinen Zahlenwert.`,
  divisionByZero: 'Teilen durch 0 ist nicht möglich.',
  currencyMix: (codes: string) => `Beträge in verschiedenen Währungen (${codes}) lassen sich nicht verrechnen.`,
  currencyUnknown: (code: string) => `Die Währung „${code}“ ist unbekannt.`,
  tooLarge: 'Das Ergebnis ist zu groß.',
  targetNotNumber: 'Formeln gehen nur für Zahlen, Beträge, Mengen und Prozent.',
} as const;

/** Abwehr übergroßer Eingaben (keine fachliche Grenze). */
export const FORMULA_MAX_LENGTH = 500;
const MAX_DEPTH = 30;
/** Interne Nachkommastellen beim Teilen und Malnehmen. */
const WORK_SCALE = 10;
/** Nachkommastellen von Zahl, Menge, Prozent (wie `edit.ts`). */
const NUMBER_SCALE = 4;
/** Größter Betrag eines Ergebnisses (Ziffern vor dem Komma), schützt vor riesigen Zahlen. */
const MAX_INT_DIGITS = 15;

const NUMERIC_TYPES: readonly ListColumnType[] = ['number', 'money', 'quantity', 'percent'];

/** Spalte, auf die eine Formel sich beziehen darf (aus dem Register der Liste). */
export interface FormulaColumn {
  key: string;
  label: string;
  type: ListColumnType;
}

export type FormulaFunction = 'runden' | 'min' | 'max' | 'wenn';
export type FormulaArithmetic = '+' | '-' | '*' | '/';
export type FormulaComparison = '<' | '<=' | '>' | '>=' | '=' | '<>';

export type FormulaNode =
  | { kind: 'number'; units: bigint; scale: number }
  | { kind: 'column'; key: string; label: string; type: ListColumnType }
  | { kind: 'negate'; value: FormulaNode }
  | { kind: 'arith'; op: FormulaArithmetic; left: FormulaNode; right: FormulaNode }
  | { kind: 'compare'; op: FormulaComparison; left: FormulaNode; right: FormulaNode }
  | { kind: 'call'; fn: FormulaFunction; args: FormulaNode[] };

export interface ParsedFormula {
  text: string;
  root: FormulaNode;
  /** Schlüssel der Spalten, auf die sich die Formel bezieht (ohne Doppel, in Reihenfolge des Auftretens). */
  references: string[];
}

export type FormulaParseResult = { ok: true; formula: ParsedFormula } | { ok: false; error: string };

// ---------------------------------------------------------------------------
// Zerlegen

type Token =
  | { t: 'num'; text: string }
  | { t: 'col'; name: string }
  | { t: 'name'; name: string }
  | { t: 'op'; op: FormulaArithmetic }
  | { t: 'cmp'; op: FormulaComparison }
  | { t: '(' }
  | { t: ')' }
  | { t: ';' };

const DIGIT = /[0-9]/;
const LETTER = /\p{L}/u;

function tokenText(token: Token): string {
  switch (token.t) {
    case 'num':
      return token.text;
    case 'col':
      return `{${token.name}}`;
    case 'name':
      return token.name;
    case 'op':
    case 'cmp':
      return token.op;
    default:
      return token.t;
  }
}

function tokenize(text: string): { ok: true; tokens: Token[] } | { ok: false; error: string } {
  const tokens: Token[] = [];
  let i = 0;
  while (i < text.length) {
    const c = text[i]!;
    if (/\s/.test(c)) {
      i += 1;
      continue;
    }
    if (DIGIT.test(c)) {
      let j = i;
      while (j < text.length && DIGIT.test(text[j]!)) j += 1;
      if ((text[j] === ',' || text[j] === '.') && DIGIT.test(text[j + 1] ?? '')) {
        j += 1;
        while (j < text.length && DIGIT.test(text[j]!)) j += 1;
      }
      tokens.push({ t: 'num', text: text.slice(i, j) });
      i = j;
      continue;
    }
    if (c === '{') {
      const end = text.indexOf('}', i + 1);
      if (end < 0) return { ok: false, error: FORMULA_TEXTS.unclosedColumn };
      const name = text.slice(i + 1, end).replace(/\s+/g, ' ').trim();
      if (name === '') return { ok: false, error: FORMULA_TEXTS.emptyColumn };
      tokens.push({ t: 'col', name });
      i = end + 1;
      continue;
    }
    if (LETTER.test(c)) {
      let j = i;
      while (j < text.length && (LETTER.test(text[j]!) || DIGIT.test(text[j]!) || text[j] === '_')) j += 1;
      tokens.push({ t: 'name', name: text.slice(i, j) });
      i = j;
      continue;
    }
    const two = text.slice(i, i + 2);
    if (two === '<=' || two === '>=' || two === '<>') {
      tokens.push({ t: 'cmp', op: two });
      i += 2;
      continue;
    }
    switch (c) {
      case '+':
        tokens.push({ t: 'op', op: '+' });
        break;
      case '-':
      case '−':
        tokens.push({ t: 'op', op: '-' });
        break;
      case '*':
      case '×':
        tokens.push({ t: 'op', op: '*' });
        break;
      case '/':
      case '÷':
        tokens.push({ t: 'op', op: '/' });
        break;
      case '<':
        tokens.push({ t: 'cmp', op: '<' });
        break;
      case '>':
        tokens.push({ t: 'cmp', op: '>' });
        break;
      case '=':
        tokens.push({ t: 'cmp', op: '=' });
        break;
      case '≤':
        tokens.push({ t: 'cmp', op: '<=' });
        break;
      case '≥':
        tokens.push({ t: 'cmp', op: '>=' });
        break;
      case '≠':
        tokens.push({ t: 'cmp', op: '<>' });
        break;
      case '(':
        tokens.push({ t: '(' });
        break;
      case ')':
        tokens.push({ t: ')' });
        break;
      case ';':
        tokens.push({ t: ';' });
        break;
      default:
        return { ok: false, error: FORMULA_TEXTS.badChar(c) };
    }
    i += 1;
  }
  return { ok: true, tokens };
}

// ---------------------------------------------------------------------------
// Dezimalzahlen über BigInt

interface Dec {
  units: bigint;
  scale: number;
}

const pow10 = (n: number): bigint => 10n ** BigInt(n);

/** Ganzzahlige Division, kaufmännisch gerundet (half-up, weg von null). */
function divHalfUp(n: bigint, d: bigint): bigint {
  const negative = n < 0n !== d < 0n;
  const an = n < 0n ? -n : n;
  const ad = d < 0n ? -d : d;
  const q = (an * 2n + ad) / (ad * 2n);
  return negative ? -q : q;
}

/** Abrunden zur nächstkleineren ganzen Zahl (auch negativ). */
function floorDiv(n: bigint, d: bigint): bigint {
  const q = n / d;
  return n % d !== 0n && n < 0n !== d < 0n ? q - 1n : q;
}

function rescale(d: Dec, scale: number): bigint {
  if (d.scale <= scale) return d.units * pow10(scale - d.scale);
  return divHalfUp(d.units, pow10(d.scale - scale));
}

/** Höchstens `WORK_SCALE` Stellen (half-up). */
function capped(d: Dec): Dec {
  return d.scale > WORK_SCALE ? { units: rescale(d, WORK_SCALE), scale: WORK_SCALE } : d;
}

function compareDec(a: Dec, b: Dec): number {
  const s = Math.max(a.scale, b.scale);
  const x = rescale(a, s);
  const y = rescale(b, s);
  return x < y ? -1 : x > y ? 1 : 0;
}

const DECIMAL_TEXT = /^(-?)(\d{1,18})(?:[.,](\d{1,10}))?$/;

function parseDecText(text: string): Dec | null {
  const m = DECIMAL_TEXT.exec(text.trim());
  if (!m) return null;
  const frac = m[3] ?? '';
  const units = BigInt(`${m[2]}${frac}`);
  return { units: m[1] === '-' ? -units : units, scale: frac.length };
}

function tooLarge(d: Dec): boolean {
  const abs = d.units < 0n ? -d.units : d.units;
  return abs >= pow10(MAX_INT_DIGITS + d.scale);
}

/** Dezimalzahl als deutscher Text: Komma, mindestens `minScale`, höchstens `maxScale` Stellen (half-up), ohne Tausenderpunkte. */
function germanText(d: Dec, minScale: number, maxScale: number): string {
  let scale = Math.min(d.scale, maxScale);
  let units = rescale(d, scale);
  // Überflüssige Nullen hinten weg, aber nicht unter `minScale`.
  while (scale > minScale && units % 10n === 0n) {
    units /= 10n;
    scale -= 1;
  }
  if (scale < minScale) {
    units *= pow10(minScale - scale);
    scale = minScale;
  }
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
  const int = digits.slice(0, digits.length - scale);
  const frac = digits.slice(digits.length - scale);
  return `${negative ? '−' : ''}${int}${scale > 0 ? `,${frac}` : ''}`;
}

// ---------------------------------------------------------------------------
// Parser

class ParseError extends Error {}

const FUNCTIONS: Record<string, FormulaFunction> = { runden: 'runden', min: 'min', max: 'max', wenn: 'wenn' };

function normalizeLabel(label: string): string {
  return label.replace(/\s+/g, ' ').trim().toLocaleLowerCase('de-DE');
}

/**
 * Prüft und zerlegt eine Formel. Spaltenbezüge nur auf `columns` (nach Beschriftung, Groß/Klein egal); unbekannte
 * Spalte, Funktion oder Zeichen → Satz für das Feld. Führt nichts aus.
 */
export function parseFormula(text: string, columns: readonly FormulaColumn[]): FormulaParseResult {
  if (typeof text !== 'string' || text.trim() === '') return { ok: false, error: FORMULA_TEXTS.empty };
  if (text.length > FORMULA_MAX_LENGTH) return { ok: false, error: FORMULA_TEXTS.tooLong(FORMULA_MAX_LENGTH) };
  const lexed = tokenize(text);
  if (!lexed.ok) return lexed;
  const byLabel = new Map<string, FormulaColumn>();
  // Gleiche Beschriftung zweimal: Bezug wäre mehrdeutig → Satz statt still der ersten Spalte.
  const ambiguous = new Set<string>();
  for (const c of columns) {
    const k = normalizeLabel(c.label);
    if (byLabel.has(k) && byLabel.get(k)!.key !== c.key) ambiguous.add(k);
    else byLabel.set(k, c);
  }
  const tokens = lexed.tokens;
  const references: string[] = [];
  let pos = 0;

  const peek = (): Token | undefined => tokens[pos];
  const next = (): Token => {
    const token = tokens[pos];
    if (!token) throw new ParseError(FORMULA_TEXTS.unexpectedEnd);
    pos += 1;
    return token;
  };
  const expect = (t: ')' | ';' | '('): void => {
    const token = peek();
    if (!token) throw new ParseError(t === ')' ? FORMULA_TEXTS.missingParen : FORMULA_TEXTS.unexpectedEnd);
    if (token.t !== t) throw new ParseError(t === ')' ? FORMULA_TEXTS.missingParen : FORMULA_TEXTS.trailing(tokenText(token)));
    pos += 1;
  };

  function additive(depth: number): FormulaNode {
    if (depth > MAX_DEPTH) throw new ParseError(FORMULA_TEXTS.tooDeep);
    let left = term(depth);
    for (let token = peek(); token?.t === 'op' && (token.op === '+' || token.op === '-'); token = peek()) {
      pos += 1;
      left = { kind: 'arith', op: token.op, left, right: term(depth) };
    }
    return left;
  }

  function term(depth: number): FormulaNode {
    let left = unary(depth);
    for (let token = peek(); token?.t === 'op' && (token.op === '*' || token.op === '/'); token = peek()) {
      pos += 1;
      left = { kind: 'arith', op: token.op, left, right: unary(depth) };
    }
    return left;
  }

  function unary(depth: number): FormulaNode {
    const token = peek();
    if (token?.t === 'op' && (token.op === '-' || token.op === '+')) {
      pos += 1;
      if (depth + 1 > MAX_DEPTH) throw new ParseError(FORMULA_TEXTS.tooDeep);
      const value = unary(depth + 1);
      return token.op === '-' ? { kind: 'negate', value } : value;
    }
    return primary(depth);
  }

  function condition(depth: number): FormulaNode {
    const left = additive(depth);
    const token = peek();
    if (token?.t !== 'cmp') throw new ParseError(FORMULA_TEXTS.conditionExpected);
    pos += 1;
    return { kind: 'compare', op: token.op, left, right: additive(depth) };
  }

  function args(depth: number, fn: FormulaFunction): FormulaNode[] {
    expect('(');
    const out: FormulaNode[] = [];
    if (peek()?.t === ')') {
      pos += 1;
      return out;
    }
    for (;;) {
      out.push(fn === 'wenn' && out.length === 0 ? condition(depth + 1) : additive(depth + 1));
      const token = peek();
      if (token?.t === ';') {
        pos += 1;
        continue;
      }
      expect(')');
      return out;
    }
  }

  function primary(depth: number): FormulaNode {
    const token = next();
    switch (token.t) {
      case 'num': {
        // „1.000“ ist im Deutschen tausend, im Punkt-Format eins: mehrdeutig → Satz statt stiller Fehlrechnung.
        if (/^\d{1,3}\.\d{3}$/.test(token.text)) throw new ParseError(FORMULA_TEXTS.ambiguousNumber(token.text));
        const d = parseDecText(token.text);
        if (!d) throw new ParseError(FORMULA_TEXTS.badNumber(token.text));
        return { kind: 'number', units: d.units, scale: d.scale };
      }
      case 'col': {
        // Eindeutiger Bezug über den Spaltenschlüssel (`{modul.feld}`, fügt die Spaltenwahl bei gleicher Beschriftung ein).
        const byKey = columns.find((c) => c.key === token.name);
        if (!byKey && ambiguous.has(normalizeLabel(token.name))) throw new ParseError(FORMULA_TEXTS.ambiguousColumn(token.name));
        const column = byKey ?? byLabel.get(normalizeLabel(token.name));
        if (!column) throw new ParseError(FORMULA_TEXTS.unknownColumn(token.name));
        if (!NUMERIC_TYPES.includes(column.type)) throw new ParseError(FORMULA_TEXTS.columnNotNumber(column.label));
        if (!references.includes(column.key)) references.push(column.key);
        return { kind: 'column', key: column.key, label: column.label, type: column.type };
      }
      case '(': {
        const inner = additive(depth + 1);
        expect(')');
        return inner;
      }
      case 'name': {
        // Nur die vier Funktionen der Allowlist (eigene Tabelle, kein Zugriff auf Objekt-Eigenschaften).
        const fn = Object.hasOwn(FUNCTIONS, token.name.toLowerCase()) ? FUNCTIONS[token.name.toLowerCase()]! : null;
        if (!fn) throw new ParseError(FORMULA_TEXTS.unknownFunction(token.name));
        if (peek()?.t !== '(') throw new ParseError(FORMULA_TEXTS.expected(`„(“ nach ${fn}`));
        const list = args(depth, fn);
        checkCall(fn, list);
        return { kind: 'call', fn, args: list };
      }
      case 'cmp':
        throw new ParseError(FORMULA_TEXTS.comparisonOutside);
      default:
        throw new ParseError(FORMULA_TEXTS.trailing(tokenText(token)));
    }
  }

  try {
    const root = additive(0);
    const rest = peek();
    if (rest) throw new ParseError(rest.t === 'cmp' ? FORMULA_TEXTS.comparisonOutside : FORMULA_TEXTS.trailing(tokenText(rest)));
    return { ok: true, formula: { text, root, references } };
  } catch (err) {
    if (err instanceof ParseError) return { ok: false, error: err.message };
    throw err;
  }
}

function checkCall(fn: FormulaFunction, args: readonly FormulaNode[]): void {
  switch (fn) {
    case 'runden': {
      if (args.length < 1 || args.length > 2) throw new ParseError(FORMULA_TEXTS.rundenArgs);
      if (args.length === 2 && roundMode(args[1]!) === null) throw new ParseError(FORMULA_TEXTS.rundenDigits);
      return;
    }
    case 'min':
    case 'max':
      if (args.length < 2) throw new ParseError(FORMULA_TEXTS.minMaxArgs(fn));
      return;
    case 'wenn':
      if (args.length !== 3) throw new ParseError(FORMULA_TEXTS.wennArgs);
      return;
  }
}

type RoundMode = { kind: 'digits'; digits: number } | { kind: 'ending'; ending: Dec };

/** Zweites Argument von `runden`: feste Zahl – ganze 0–4 (Stellen) oder 0 < n < 1 mit höchstens 2 Stellen (Endung). */
function roundMode(node: FormulaNode): RoundMode | null {
  if (node.kind !== 'number') return null;
  if (node.scale === 0 || node.units % pow10(node.scale) === 0n) {
    const digits = node.units / pow10(node.scale);
    return digits >= 0n && digits <= 4n ? { kind: 'digits', digits: Number(digits) } : null;
  }
  const units = rescale({ units: node.units, scale: node.scale }, 2);
  // Nur genau darstellbare Endungen (0,90 · 0,99 · 0,5), keine dritte Stelle.
  if (node.units < 0n || units <= 0n || units >= 100n || rescale({ units, scale: 2 }, node.scale) !== node.units) return null;
  return { kind: 'ending', ending: { units, scale: 2 } };
}

// ---------------------------------------------------------------------------
// Rechnen

/** Wert einer Spalte, wie ihn die Liste liefert: Zahl/Dezimaltext; Betrag `{ amountMinor, currency }`; leer `null`. */
export type FormulaValue = string | number | bigint | { amountMinor: string; currency: string } | null | undefined;

export interface FormulaTarget {
  type: ListColumnType;
  /** Währung der Zeile bei Ziel `money` (sonst egal); Ergebnis in ihrer kleinsten Einheit. */
  currency?: string | null;
}

export type FormulaResult =
  | {
      ok: true;
      /** Wert in der Form der Zielspalte (wie `edit.ts`): Betrag in der kleinsten Einheit, sonst Dezimaltext. */
      value: string;
      /** Beispielrechnung, z. B. „12,00 × 1,8 = 21,60 → 21,90“. */
      explain: string;
    }
  | { ok: false; error: string };

class EvalError extends Error {}

interface EvalContext {
  values: Readonly<Record<string, FormulaValue>>;
  currencies: Set<string>;
}

function columnValue(node: Extract<FormulaNode, { kind: 'column' }>, ctx: EvalContext): Dec {
  const raw = Object.hasOwn(ctx.values, node.key) ? ctx.values[node.key] : undefined;
  if (raw === null || raw === undefined || raw === '') throw new EvalError(FORMULA_TEXTS.valueEmpty(node.label));
  if (typeof raw === 'object') {
    const digits = findCurrencyMinorUnits(String(raw.currency));
    if (digits === null) throw new EvalError(FORMULA_TEXTS.currencyUnknown(String(raw.currency)));
    const minor = /^-?\d{1,20}$/.test(String(raw.amountMinor)) ? BigInt(raw.amountMinor) : null;
    if (minor === null) throw new EvalError(FORMULA_TEXTS.valueInvalid(node.label));
    ctx.currencies.add(String(raw.currency));
    return { units: minor, scale: digits };
  }
  if (typeof raw === 'bigint') return { units: raw, scale: 0 };
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) throw new EvalError(FORMULA_TEXTS.valueInvalid(node.label));
    const d = parseDecText(Number.isInteger(raw) ? raw.toFixed(0) : String(raw));
    if (!d) throw new EvalError(FORMULA_TEXTS.valueInvalid(node.label));
    return d;
  }
  const d = parseDecText(String(raw));
  if (!d) throw new EvalError(FORMULA_TEXTS.valueInvalid(node.label));
  return d;
}

function roundWith(value: Dec, mode: RoundMode | null): Dec {
  if (mode === null || mode.kind === 'digits') {
    const digits = mode?.digits ?? 0;
    return { units: rescale(value, digits), scale: digits };
  }
  // Nächstgelegener Wert mit der Endung (Gleichstand aufwärts): floor((x − e + 1/2) / 1) + e, auf der Skala der Endung.
  const scale = Math.max(value.scale, mode.ending.scale);
  const x = rescale(value, scale);
  const e = rescale(mode.ending, scale);
  const unit = pow10(scale);
  return { units: floorDiv(x - e + unit / 2n, unit) * unit + e, scale };
}

function evalNode(node: FormulaNode, ctx: EvalContext): Dec {
  switch (node.kind) {
    case 'number':
      return { units: node.units, scale: node.scale };
    case 'column':
      return columnValue(node, ctx);
    case 'negate': {
      const v = evalNode(node.value, ctx);
      return { units: -v.units, scale: v.scale };
    }
    case 'arith': {
      const a = evalNode(node.left, ctx);
      const b = evalNode(node.right, ctx);
      let out: Dec;
      switch (node.op) {
        case '+':
        case '-': {
          const s = Math.max(a.scale, b.scale);
          const x = rescale(a, s);
          const y = rescale(b, s);
          out = { units: node.op === '+' ? x + y : x - y, scale: s };
          break;
        }
        case '*':
          out = capped({ units: a.units * b.units, scale: a.scale + b.scale });
          break;
        case '/':
          if (b.units === 0n) throw new EvalError(FORMULA_TEXTS.divisionByZero);
          // a / b mit WORK_SCALE Stellen: a·10^(W + sb − sa) / b (a.scale ≤ W, daher Exponent ≥ 0).
          out = { units: divHalfUp(a.units * pow10(WORK_SCALE + b.scale - a.scale), b.units), scale: WORK_SCALE };
          break;
      }
      if (tooLarge(out)) throw new EvalError(FORMULA_TEXTS.tooLarge);
      return out;
    }
    case 'compare':
      // Nur als erster Teil von `wenn` (der Parser lässt es anders nicht zu).
      throw new EvalError(FORMULA_TEXTS.comparisonOutside);
    case 'call': {
      switch (node.fn) {
        case 'runden':
          return roundWith(evalNode(node.args[0]!, ctx), node.args[1] ? roundMode(node.args[1]) : null);
        case 'min':
        case 'max': {
          const values = node.args.map((a) => evalNode(a, ctx));
          return values.reduce((best, v) => {
            const c = compareDec(v, best);
            return (node.fn === 'min' ? c < 0 : c > 0) ? v : best;
          });
        }
        case 'wenn':
          return evalNode(testCondition(node.args[0]!, ctx) ? node.args[1]! : node.args[2]!, ctx);
      }
    }
  }
}

function testCondition(node: FormulaNode, ctx: EvalContext): boolean {
  if (node.kind !== 'compare') throw new EvalError(FORMULA_TEXTS.conditionExpected);
  const c = compareDec(evalNode(node.left, ctx), evalNode(node.right, ctx));
  switch (node.op) {
    case '<':
      return c < 0;
    case '<=':
      return c <= 0;
    case '>':
      return c > 0;
    case '>=':
      return c >= 0;
    case '=':
      return c === 0;
    case '<>':
      return c !== 0;
  }
}

// ---------------------------------------------------------------------------
// Beispielrechnung

const OP_TEXT: Record<FormulaArithmetic, string> = { '+': '+', '-': '−', '*': '×', '/': '÷' };
const CMP_TEXT: Record<FormulaComparison, string> = { '<': '<', '<=': '≤', '>': '>', '>=': '≥', '=': '=', '<>': '≠' };
const PRECEDENCE: Record<FormulaArithmetic, number> = { '+': 1, '-': 1, '*': 2, '/': 2 };

/** Formel mit eingesetzten Werten (Beträge mit 2 bzw. den Stellen ihrer Währung, Zahlen wie eingegeben). */
function renderNode(node: FormulaNode, ctx: EvalContext, parent = 0, right = false): string {
  switch (node.kind) {
    case 'number':
      return germanText({ units: node.units, scale: node.scale }, node.scale, node.scale);
    case 'column': {
      // Nicht gebrauchter Zweig von `wenn` darf leer sein: dort steht der Name statt eines Werts.
      let v: Dec;
      try {
        v = columnValue(node, ctx);
      } catch {
        return `{${node.label}}`;
      }
      return node.type === 'money' ? germanText(v, v.scale, v.scale) : germanText(v, 0, NUMBER_SCALE);
    }
    case 'negate':
      return `−${renderNode(node.value, ctx, 3)}`;
    case 'arith': {
      const p = PRECEDENCE[node.op];
      const text = `${renderNode(node.left, ctx, p)} ${OP_TEXT[node.op]} ${renderNode(node.right, ctx, p, true)}`;
      return p < parent || (p === parent && right) ? `(${text})` : text;
    }
    case 'compare':
      return `${renderNode(node.left, ctx)} ${CMP_TEXT[node.op]} ${renderNode(node.right, ctx)}`;
    case 'call':
      return `${node.fn}(${node.args.map((a) => renderNode(a, ctx)).join('; ')})`;
  }
}

/**
 * Nachkommastellen eines Werts der Zielspalte (Betrag: Stellen der Währung, sonst 4). Ist die Währung der Zeile nicht
 * bekannt (z. B. Betrag noch leer), gilt die Währung der Beträge in der Formel; ohne solche 2 Stellen.
 */
function targetScale(target: FormulaTarget, currencies: ReadonlySet<string>): number {
  if (target.type !== 'money') return NUMBER_SCALE;
  const code = target.currency ?? (currencies.size === 1 ? [...currencies][0]! : null);
  const digits = code ? findCurrencyMinorUnits(code) : 2;
  if (digits === null) throw new EvalError(FORMULA_TEXTS.currencyUnknown(String(code)));
  return digits;
}

/**
 * Rechnet eine geprüfte Formel für eine Zeile. `values`: Werte der Bezugsspalten (Schlüssel → Wert wie in der Liste).
 * Ergebnis in der Form der Zielspalte und die Beispielrechnung; leerer Bezug, Teilen durch 0, gemischte Währungen →
 * Satz statt Wert.
 */
export function evaluateFormula(formula: ParsedFormula, values: Readonly<Record<string, FormulaValue>>, target: FormulaTarget): FormulaResult {
  if (!NUMERIC_TYPES.includes(target.type)) return { ok: false, error: FORMULA_TEXTS.targetNotNumber };
  const ctx: EvalContext = { values, currencies: new Set() };
  try {
    // Äußeres `runden` getrennt zeigen: „12,00 × 1,8 = 21,60 → 21,90“.
    const root = formula.root;
    const outerRound = root.kind === 'call' && root.fn === 'runden' ? root : null;
    const inner = outerRound ? evalNode(outerRound.args[0]!, ctx) : evalNode(root, ctx);
    const result = outerRound ? roundWith(inner, outerRound.args[1] ? roundMode(outerRound.args[1]) : null) : inner;
    if (target.type === 'money' && target.currency) ctx.currencies.add(target.currency);
    if (ctx.currencies.size > 1) return { ok: false, error: FORMULA_TEXTS.currencyMix([...ctx.currencies].sort().join(', ')) };
    const scale = targetScale(target, ctx.currencies);
    const units = rescale(result, scale);
    if (tooLarge({ units, scale })) return { ok: false, error: FORMULA_TEXTS.tooLarge };
    const value = target.type === 'money' ? units.toString() : plainText({ units, scale });
    const money = target.type === 'money';
    const show = (d: Dec) => germanText(d, money ? scale : 0, Math.max(scale, money ? 4 : NUMBER_SCALE));
    const rendered = renderNode(outerRound ? outerRound.args[0]! : root, ctx);
    const final = show({ units, scale });
    const explain = outerRound ? `${rendered} = ${show(inner)} → ${final}` : `${rendered} = ${final}`;
    return { ok: true, value, explain };
  } catch (err) {
    if (err instanceof EvalError) return { ok: false, error: err.message };
    throw err;
  }
}

/** Dezimaltext mit Punkt ohne überflüssige Nullen (Form der Zahlen-Spalten in `edit.ts`). */
function plainText(d: Dec): string {
  return germanText(d, 0, d.scale).replace('−', '-').replace(',', '.');
}

/** Spalten, auf die sich eine Formel beziehen darf (Zahlentypen der Liste). */
export function formulaColumns<T extends FormulaColumn>(columns: readonly T[]): T[] {
  return columns.filter((c) => NUMERIC_TYPES.includes(c.type));
}

/** Passt „Formel“ zur Zielspalte? Nur Zahl, Betrag, Menge, Prozent. */
export function formulaFitsType(type: ListColumnType): boolean {
  return NUMERIC_TYPES.includes(type);
}
